import { PlayingAsset, PlayingMode, NpawAnalytics } from 'types/Player';
import { YouboraOptions, YouboraAnalyticsOptions, InitOptions, ContentLinkType } from 'types/Tracking';
import {
    isCatchupPlayerAsset,
    isLivePlayerAsset,
    isRecordingPlayerAsset,
    isTrailerPlayerAsset,
    isVodPlayerAsset,
} from 'utils/fnTypeGuards';
import npawAdapterConfig from 'config/customConfig/youbora-adapter.json';
import { NPAW_PAGE_RULES, resolvePageName } from 'config/npawPageRules';
import NpawPlugin from 'npaw-plugin-es5';

enum YouboraPlaybackTypes {
    LIVE = 'Live',
    TIMESHIFT = 'Timeshift',
    CATCHUP = 'Catchup',
    VOD = 'VOD',
    TRAILER = 'Trailer',
    RECORDING = 'Recording',
    UNKNOWN = 'Unknown',
}

class YouboraTracking {
    private static instance: YouboraTracking;

    private youboraPlugin = null;

    private initialized = false;

    private currentUserId: string | null = null;

    private currentProfileId: string | null = null;

    private lastTrackedPath: string | null = null;

    private lastTrackedPageName: string | null = null;

    private sessionClosed = false;

    private playbackStarted = false;

    private isAttemptingRecovery = false;

    private recoveryTimeoutId: number | null = null;

    private readonly RECOVERY_TIMEOUT = 10000;

    private readonly VIDEO_KEY = 'default';

    private lastAccountCode: string = '';

    private lastInitOptions: InitOptions | undefined;

    private trackedContentIds: Set<string> = new Set();

    private trackingTimeout: Map<string, number> = new Map();

    private currentAssetId: string | null = null;

    private adapterEngine: any = null;

    private customDimensionOption: Record<string, any> | null = null;

    private serverVideoOptions: Record<string, any> | null = null;

    public get isInitialized(): boolean {
        return this.initialized;
    }

    public get hasPlaybackStarted(): boolean {
        return this.playbackStarted;
    }

    // Retry tracking up to 5 times with 500ms delays until productAnalytics is ready. Small hack for "Content Play"
    private attemptTrackPlay(
        contentID: string,
        attempt: number,
        customParams?: { contentLink?: string; contentLinkType?: ContentLinkType },
        trackingKey?: string
    ): void {
        const maxAttempts = 5;
        const keyToUse = trackingKey || contentID;

        if (!this.youboraPlugin?.productAnalytics) {
            if (attempt < maxAttempts) {
                setTimeout(() => {
                    this.attemptTrackPlay(contentID, attempt + 1, customParams, trackingKey);
                }, 500);
                return;
            }

            this.trackingTimeout.delete(keyToUse);
            return;
        }

        try {
            if (customParams?.contentLinkType) {
                this.youboraPlugin.productAnalytics.trackPlay(contentID, customParams);
            } else {
                this.youboraPlugin.productAnalytics.trackPlay(contentID);
            }
            this.trackedContentIds.add(keyToUse);
            this.trackingTimeout.delete(keyToUse);
        } catch {
            if (attempt < maxAttempts) {
                setTimeout(() => {
                    this.attemptTrackPlay(contentID, attempt + 1, customParams, trackingKey);
                }, 500);
                return;
            }

            this.trackingTimeout.delete(keyToUse);
        }
    }

    static getInstance(): YouboraTracking {
        if (!YouboraTracking.instance) {
            YouboraTracking.instance = new YouboraTracking();
        }
        return YouboraTracking.instance;
    }

    initializePlugin(accountCode: string, options?: InitOptions) {
        this.lastAccountCode = accountCode;
        this.lastInitOptions = options;

        try {
            this.youboraPlugin = new NpawPlugin(accountCode);
            this.adapterEngine = null;

            this.youboraPlugin.setLogLevel(6);

            const analyticsOptions: YouboraAnalyticsOptions = {
                'app.name': options?.appName || '',
                'app.releaseVersion': options?.appVersion || '',
                checkVideoBufferInPlayheadMonitor: true,
                ...(options?.userId && { userId: options.userId }),
            };

            this.youboraPlugin.setAnalyticsOptions(analyticsOptions);

            if (this.youboraPlugin.productAnalytics) {
                this.youboraPlugin.productAnalytics.initialize({
                    autoTrackNavigation: false,
                    autoTrackAttribution: true,
                    highlightContentAfter: 1000,
                    pageRules: NPAW_PAGE_RULES,
                });

                this.lastTrackedPath = null;
                this.lastTrackedPageName = null;
            }

            this.initialized = true;
        } catch (error) {
            this.initialized = false;
            this.youboraPlugin = null;
            console.error('NPAW - Initialization error:', error);
            throw error;
        }
    }

    setUserId(userId: string) {
        if (!this.youboraPlugin) return;

        try {
            this.youboraPlugin.setAnalyticsOptions({ userId });
            this.currentUserId = userId;
        } catch (error) {
            console.error('NPAW - Error setting userId:', error);
        }
    }

    handleLoginStateChange(isLoggedIn: boolean, userId?: string, force: boolean = false) {
        if (!this.youboraPlugin?.productAnalytics) {
            console.log('NPAW - productAnalytics is not available');
            return;
        }

        try {
            if (isLoggedIn && userId) {
                if (this.currentUserId !== userId || force) {
                    this.youboraPlugin.productAnalytics.loginSuccessful(userId);
                    this.currentUserId = userId;
                }
            }
        } catch (error) {
            console.error('NPAW - Error handling login state:', error);
        }
    }

    trackContentFocusIn(section: string, sectionOrder: number, column: number, row: number, contentId: string) {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available');
            return;
        }

        try {
            this.youboraPlugin.productAnalytics.contentFocusIn(section, sectionOrder, column, row, contentId);
        } catch (error) {
            console.error('NPAW - Error in contentFocusIn:', error);
        }
    }

    trackContentFocusOut() {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available');
            return;
        }

        try {
            this.youboraPlugin.productAnalytics.contentFocusOut();
        } catch (error) {
            console.error('NPAW - Error in contentFocusOut:', error);
        }
    }

    trackContentClick(
        section: string,
        sectionOrder: number,
        column: number,
        row: number,
        contentId: string,
        customParams?: { contentLink?: string; contentLinkType?: ContentLinkType }
    ) {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available');
            return;
        }

        try {
            if (customParams?.contentLink && customParams?.contentLinkType) {
                this.youboraPlugin.productAnalytics.trackContentClick(section, sectionOrder, column, row, contentId, customParams);
            } else {
                this.youboraPlugin.productAnalytics.trackContentClick(section, sectionOrder, column, row, contentId);
            }
        } catch (error) {
            console.error('NPAW - Error in trackContentClick:', error);
        }
    }

    trackContentPlay(contentID: string, customParams?: { contentLink?: string; contentLinkType?: ContentLinkType }): void {
        // Only clear existing tracking if we're switching to a completely different asset (not 'null' metadata calls)
        const isMetadataCall = contentID === 'null';
        const isNewAsset =
            this.currentAssetId !== null && this.currentAssetId !== contentID && !isMetadataCall && this.currentAssetId !== 'null';

        if (isNewAsset) {
            this.trackingTimeout.forEach((timeout) => clearTimeout(timeout));
            this.trackingTimeout.clear();
            this.trackedContentIds.clear();
        }

        // Update currentAssetId only for non-metadata calls
        if (!isMetadataCall) {
            this.currentAssetId = contentID;
        }

        // Use contentLinkType even if contentLink is empty string to ensure uniqueness
        const trackingKey =
            contentID === 'null' && customParams?.contentLinkType
                ? `${customParams.contentLinkType}:${customParams.contentLink || ''}`
                : contentID;

        if (this.trackedContentIds.has(trackingKey)) {
            return;
        }

        const existingTimeout = this.trackingTimeout.get(trackingKey);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        // Small delay for tracking content play because sometimes the player gets stuck at loading manifest OR stop event is not triggered fast enough
        const timeoutId = window.setTimeout(() => {
            this.attemptTrackPlay(contentID, 0, customParams, trackingKey);
        }, 2000);

        this.trackingTimeout.set(trackingKey, timeoutId);
    }

    // Method to reset tracking for a specific asset
    resetContentTracking(contentID: string): void {
        this.trackedContentIds.delete(contentID);
        const timeout = this.trackingTimeout.get(contentID);
        if (timeout) {
            clearTimeout(timeout);
            this.trackingTimeout.delete(contentID);
        }
    }

    handleLogout() {
        if (!this.youboraPlugin?.productAnalytics) return;

        try {
            this.youboraPlugin.productAnalytics.logout();
            this.currentUserId = null;
            this.currentProfileId = null;
            this.trackedContentIds.clear();
            this.trackingTimeout.forEach((timeout) => clearTimeout(timeout));
            this.trackingTimeout.clear();
            this.currentAssetId = null;
            this.customDimensionOption = null;
            this.serverVideoOptions = null;
        } catch (error) {
            console.error('Error during logout:', error);
        }
    }

    setOptions = (options: YouboraOptions) => {
        if (!this.isInitialized || !this.youboraPlugin) return;

        const merged: Record<string, any> = { ...options, ...(this.customDimensionOption || {}) };

        this.youboraPlugin.setVideoOptions(merged);
    };

    setServerAnalytics = (npaw: NpawAnalytics) => {
        if (!this.isInitialized || !this.youboraPlugin) return;

        const videoOptions: Record<string, any> = {};

        if (npaw['content.transactionCode']) {
            videoOptions['content.transactionCode'] = npaw['content.transactionCode'];
        }

        if (npaw['content.customDimension.1']) {
            videoOptions['content.customDimension.1'] = npaw['content.customDimension.1'];
            this.customDimensionOption = { 'content.customDimension.1': npaw['content.customDimension.1'] };
        }

        if (Object.keys(videoOptions).length > 0) {
            this.serverVideoOptions = videoOptions;
            this.youboraPlugin.setVideoOptions(videoOptions);
        }
    };

    handleAppExit() {
        if (!this.youboraPlugin?.productAnalytics) return;

        try {
            this.youboraPlugin.productAnalytics.endSession();
        } catch (error) {
            console.error('NPAW - Error ending session:', error);
        }
    }

    static getPlaybackType = (asset: PlayingAsset, playingMode?: PlayingMode): YouboraPlaybackTypes => {
        if (isLivePlayerAsset(asset)) {
            return playingMode === PlayingMode.NORMAL || !playingMode ? YouboraPlaybackTypes.LIVE : YouboraPlaybackTypes.TIMESHIFT;
        }

        if (isRecordingPlayerAsset(asset)) return YouboraPlaybackTypes.RECORDING;
        if (isTrailerPlayerAsset(asset)) return YouboraPlaybackTypes.TRAILER;
        if (isVodPlayerAsset(asset)) return YouboraPlaybackTypes.VOD;
        if (isCatchupPlayerAsset(asset)) return YouboraPlaybackTypes.CATCHUP;

        return YouboraPlaybackTypes.UNKNOWN;
    };

    setShakaAdapter = (engine: any) => {
        if (this.adapterEngine !== engine) {
            this.youboraPlugin?.registerAdapterFromString(
                engine,
                JSON.stringify(npawAdapterConfig),
                this.serverVideoOptions,
                this.VIDEO_KEY
            );

            this.adapterEngine = engine;
            this.applyRecoveryErrorOverrides();
        }

        if (this.serverVideoOptions) {
            this.youboraPlugin?.setVideoOptions(this.serverVideoOptions);
        }
    };

    private applyRecoveryErrorOverrides = () => {
        const adapter = this.youboraPlugin?.getAdapter(this.VIDEO_KEY);

        if (!adapter) {
            console.warn('NPAW: Failed to get adapter');
            return;
        }

        const originalFireError = adapter.fireError;
        const originalFireFatalError = adapter.fireFatalError;

        // Overrides fireError to prevent fatal errors during recovery mode
        adapter.fireError = (errorCode, errorMessage, metadata) => {
            // If we're in recovery mode, all errors are non-fatal :)
            if (this.isAttemptingRecovery) {
                const nonFatalMetadata = {
                    ...metadata,
                    isFatal: false,
                };
                originalFireError.call(adapter, errorCode, errorMessage, nonFatalMetadata);
            } else {
                originalFireError.call(adapter, errorCode, errorMessage, metadata);
            }
        };

        adapter.fireFatalError = (errorCode, errorMessage, metadata) => {
            if (this.isAttemptingRecovery) {
                adapter.fireError(errorCode, errorMessage, {
                    ...metadata,
                    isFatal: false,
                });
            } else {
                originalFireFatalError.call(adapter, errorCode, errorMessage, metadata);
            }
        };
    };

    stopView = () => {
        if (!this.isInitialized || !this.youboraPlugin) return;

        try {
            this.youboraPlugin.getAdapter(this.VIDEO_KEY)?.fireStop();
        } catch (error) {
            console.error('NPAW - Error stopping view:', error);
        }
    };

    // If network error occurs:
    startRecoveryAttempt = () => {
        this.isAttemptingRecovery = true;

        if (this.recoveryTimeoutId !== null) {
            window.clearTimeout(this.recoveryTimeoutId);
        }

        this.recoveryTimeoutId = window.setTimeout(() => {
            this.isAttemptingRecovery = false;
            this.recoveryTimeoutId = null;
        }, this.RECOVERY_TIMEOUT);
    };

    endRecoveryAttempt = () => {
        this.isAttemptingRecovery = false;

        if (this.recoveryTimeoutId !== null) {
            window.clearTimeout(this.recoveryTimeoutId);
            this.recoveryTimeoutId = null;
        }
    };

    setPlaybackStarted = (started: boolean) => {
        this.playbackStarted = started;
    };

    setChronos = (durations: Record<string, number>) => {
        if (!this.isInitialized || !this.youboraPlugin) return;

        try {
            Object.keys(durations).forEach((name) => {
                this.youboraPlugin.setChronoDuration(name, durations[name], this.VIDEO_KEY);
            });
        } catch (error) {
            console.error('NPAW - Error setting chronos:', error);
        }
    };

    fireFatalError = (code: number, message: string, metadata: string) => {
        this.youboraPlugin?.getAdapter().fireFatalError(code, message, metadata, undefined);
    };

    trackPageNavigation(path: string, pageName?: string) {
        if (!this.youboraPlugin?.productAnalytics) return;

        try {
            const decodedPath = decodeURIComponent(path);

            const normalizedPath = decodedPath.startsWith('/') ? decodedPath : `/${decodedPath}`;

            const normalizedLastPath = this.lastTrackedPath?.startsWith('/')
                ? this.lastTrackedPath
                : this.lastTrackedPath
                  ? `/${this.lastTrackedPath}`
                  : null;

            const resolvedPageName = pageName || resolvePageName(normalizedPath) || undefined;

            if (normalizedPath === normalizedLastPath && resolvedPageName === (this.lastTrackedPageName ?? undefined)) {
                return;
            }

            const currentRoute = `${window.location.origin}${normalizedPath}`;

            const customDimensions: {
                paRoute: string;
                paFullRoute: string;
                paRouteDomain: string;
                paPage?: string;
            } = {
                paRoute: normalizedPath,
                paFullRoute: currentRoute,
                paRouteDomain: window.location.hostname,
            };

            if (resolvedPageName) {
                customDimensions.paPage = resolvedPageName;
                this.youboraPlugin.productAnalytics.trackNavByName(resolvedPageName, customDimensions);
            } else {
                this.youboraPlugin.productAnalytics.trackNavByRoute(currentRoute, customDimensions);
            }

            this.lastTrackedPath = normalizedPath;
            this.lastTrackedPageName = resolvedPageName ?? null;
        } catch (error) {
            console.error('NPAW - Error tracking navigation:', error);
        }
    }

    trackPlayerInteraction(interactionName: string, contentId?: string): void {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available');
            return;
        }

        try {
            if (contentId && contentId.trim() !== '') {
                this.youboraPlugin.productAnalytics.trackPlayerInteraction(interactionName, { contentID: contentId });
            } else {
                this.youboraPlugin.productAnalytics.trackPlayerInteraction(interactionName);
            }
        } catch (error) {
            console.error('NPAW - Error in trackPlayerInteraction:', error);
        }
    }

    trackSearchQuery(query: string): void {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available');
            return;
        }

        try {
            this.youboraPlugin.productAnalytics.trackSearchQuery(query);
        } catch (error) {
            console.error('NPAW - Error in trackSearchQuery:', error);
        }
    }

    trackSearchResult(resultCount: number, query: string): void {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available');
            return;
        }

        try {
            this.youboraPlugin.productAnalytics.trackSearchResult(resultCount, query);
        } catch (error) {
            console.error('NPAW - Error in trackSearchResult:', error);
        }
    }

    trackSearchClick(
        section: string,
        sectionOrder: number,
        column: number,
        row: number,
        contentId: string,
        query: string,
        customParams?: { contentLink?: string; contentLinkType?: ContentLinkType }
    ): void {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available');
            return;
        }

        try {
            if (customParams?.contentLink && customParams?.contentLinkType) {
                this.youboraPlugin.productAnalytics.trackSearchClick(section, sectionOrder, column, row, contentId, query, customParams);
            } else {
                this.youboraPlugin.productAnalytics.trackSearchClick(section, sectionOrder, column, row, contentId, query);
            }
        } catch (error) {
            console.error('NPAW - Error in trackSearchClick:', error);
        }
    }

    trackEngagement(eventName: string, contentId: string): void {
        if (!this.youboraPlugin?.productAnalytics) {
            console.error('NPAW - productAnalytics is not available for engagement tracking');
            return;
        }

        try {
            this.youboraPlugin.productAnalytics.trackEngagementEvent(eventName, contentId);
        } catch (error) {
            console.error('NPAW - Error tracking engagement:', error);
        }
    }
}

export default YouboraTracking;
